"use client"

import { X, FileText } from "lucide-react"
import { useState, useRef } from "react"
import  DeleteIcon  from "../../../assets/icons/delete-icon.svg"
import Button from "../../shared/Button"
import Tooltip from "../../shared/Tooltip"
import { InfoTooltip } from "../../shared/InfoTooltip"


/**
 * Step1ImportDocModal Component
 * A modal for importing a .docx file with three distinct states:
 * 1. Initial: Drag and drop or browse to select a file.
 * 2. Uploading: A simulated progress bar shows file being processed.
 * 3. Uploaded: A success message indicates the file is ready.
 *
 * @param {boolean} isOpen - Controls the visibility of the modal.
 * @param {function} onClose - Callback function to close the modal.
 * @param {function} onFileSelect - Callback when a file is selected (receives the file object).
 * @param {function} onNextStep - Callback to proceed to the next step after a file is uploaded (receives the file object).
 */
export default function Step1ImportDocModal({ isOpen, onClose, onFileSelect, onNextStep }) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadStatus, setUploadStatus] = useState("initial")
  const fileInputRef = useRef(null)

  if (!isOpen) return null

  // const handleBackdropClick = (e) => {
  //   if (e.target === e.currentTarget) onClose()
  // }

  const isDocxFile = (file) => {
    if (!file) return false;
    const docxMime = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    return file.type === docxMime || /\.docx$/i.test(file.name || "");
  };

  const handleFileSelect = (file) => {
    if (isDocxFile(file)) {
      setSelectedFile(file)
      setUploadStatus("uploading")
      if (onFileSelect) onFileSelect(file)

      // Simulate a file upload delay using a timeout
      setTimeout(() => {
        setUploadStatus("uploaded")
      }, 2000)
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files[0]
    if (file) handleFileSelect(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    let file = null
    const dt = e.dataTransfer
    if (dt.items && dt.items.length > 0) {
      const item = dt.items[0]
      if (item.kind === "file") file = item.getAsFile()
    } else if (dt.files && dt.files.length > 0) {
      file = dt.files[0]
    }
    if (file) handleFileSelect(file)
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleNextStep = () => {
    if (selectedFile && onNextStep) {
      onNextStep(selectedFile)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      // onClick={handleBackdropClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Backdrop */}
      <div className={`absolute inset-0 transition-colors ${isDragOver ? "bg-black/70" : "bg-black/60"}`} />

      {/* Modal - Made responsive with max-width and full width on mobile */}
      <div className="relative z-10 flex flex-col w-full max-w-[490px] rounded-[18px] border border-[#E5E5E5] bg-white shadow-lg px-4 sm:px-8 py-6 sm:py-[30px] mx-4">
        {/* Close button - Adjusted spacing for mobile */}
        <div className="flex justify-between items-center mb-6 sm:mb-[30px]">
          <div className="flex h-[28px] px-2 py-2 justify-center items-center gap-[10px] rounded-[8px] bg-[#F2F3F7]">
            <p className="text-xs font-semibold text-dark-blue ">Step 1</p>
          </div>
          <button onClick={onClose} className="w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors">
            <X className="w-3 h-3 text-white" />
          </button>
        </div>

        {/* Heading + description - Responsive text sizes and spacing */}
        <div className="flex flex-col items-start gap-2 mb-6 sm:mb-[30px]">
          <div className='flex items-center gap-1 sm:gap-2'>
            <h2 className="text-xl sm:text-[25px] font-bold text-[#180A63]">Upload document</h2>
            <Tooltip content="Upload an existing draft. WriteForm will apply APA style and generate the full structure automatically">
                <InfoTooltip />
            </Tooltip>
          </div>
          <p className="text-sm sm:text-[14px] leading-5 text-[#686576]">
            Upload your draft — we’ll shape it into a complete APA-style document.
          </p>
        </div>

        {/* Divider - Responsive negative margins */}
        <div className="h-[1px] bg-[#F0F0F0] -mx-4 sm:-mx-8 mb-6 sm:mb-[30px]" />

        {/* Conditional content based on upload status */}
        {uploadStatus === "initial" && (
          <>
            {/* Drag and drop area - Initial state - Responsive padding and spacing */}
            <div
              className={`w-full px-6 sm:px-[49px] py-8 sm:py-[52px] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-3 cursor-pointer bg-[#FBF8FD] transition mb-6 sm:mb-[30px] ${
                isDragOver ? "border-purple bg-[#F7F2FF]" : "border-purple"
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
            >
              <FileText className="w-12 h-12 sm:w-14 sm:h-14 text-[#B67FE2]" />
              <p className="text-sm text-gray text-center">
                
                Click to <span className="underline cursor-pointer font-medium text-dark-blue">upload a .docx file</span>  from your device, or drag and drop it here.
              </p>
              {/* <p className="text-sm text-gray text-center">
                <span className="text-dark-blue font-medium ">Drag and drop your .docx file here,</span> or click to
                browse your device. Other file types are not supported.
              </p> */}
            </div>
            {/* Hidden input */}
            <input ref={fileInputRef} type="file" accept=".docx" onChange={handleFileInputChange} className="hidden" />
          </>
        )}

        {uploadStatus === "uploading" && (
          // Uploading state with progress bar - Made responsive width and padding
          <div className="w-full px-6 sm:px-[40px] py-6 sm:py-[25px] rounded-[8px] border border-dashed border-[#B67FE2] bg-[#FBF8FD] flex flex-col items-center justify-center gap-3 transition mb-6 sm:mb-[30px]">
            <div className="flex items-center gap-3 sm:gap-4 w-full">
              <div className="flex items-center justify-center w-[45px] h-[45px] sm:w-[50px] sm:h-[50px] rounded-[10px] border-[1px] border-[#F7EAFF] opacity-100">
                <FileText className="w-6 h-7 sm:w-7 sm:h-9 m-0 text-purple" />
              </div>
              <div className="flex-grow min-w-0">
                {/* Filename - Added text truncation for long filenames */}
                <p className="text-sm sm:text-base text-dark-blue font-medium truncate">
                  {selectedFile?.name || "Importing document..."}
                </p>
                {/* File size */}
                <p className="text-xs sm:text-sm text-gray">
                  {selectedFile ? `${(selectedFile.size / 1024).toFixed(0)}KB` : "200KB"}
                </p>
              </div>
              <button onClick={() => setUploadStatus("initial")} className="flex-shrink-0">
                  <img src={DeleteIcon} alt="Delete" className="w-6 h-6" />
              </button>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
              <div className="h-full bg-purple animate-pulse" />
            </div>
          </div>
        )}

        {uploadStatus === "uploaded" && (
          // Uploaded state with success message - Made responsive width and padding
          <>
            <div className="w-full px-6 sm:px-[40px] py-6 sm:py-[25px] rounded-[8px] border border-dashed border-[#B67FE2] bg-[#FBF8FD] flex flex-col items-center justify-center gap-3 mb-5 sm:mb-[20px] transition">
              <div className="flex items-center gap-3 sm:gap-4 w-full">
                <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-purple flex-shrink-0" />
                <div className="flex-grow min-w-0">
                  <p className="text-sm sm:text-base text-gray font-medium truncate">
                    {selectedFile?.name || "Document imported successfully."}
                  </p>
                 <p className="text-xs sm:text-sm text-gray">
                  {selectedFile ? `${(selectedFile.size / 1024).toFixed(0)}KB` : "200KB"}
                </p>
                </div>
                <button onClick={() => setUploadStatus("initial")} className="flex-shrink-0">
                  <img src={DeleteIcon} alt="Delete" className="w-6 h-6" />
                </button>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-purple" />
              </div>
            </div>
          </>
        )}

        {uploadStatus === "uploaded" && (
          <div className="mb-6 sm:mb-[30px] px-1">
            <p className="text-sm text-dark-blue font-semibold leading-6">
              Not the right file?{" "}
              <span className="text-sm font-normal text-gray leading-6">
                Remove it and upload a different draft before continuing to the next step.
              </span>
            </p>
            {/* <p className="text-sm text-gray leading-6 mt-2">Proceed to choose your style guide and apply formatting.</p> */}
          </div>
        )}

        {/* Divider - Responsive negative margins */}
        <div className="h-[1px] bg-[#F0F0F0] -mx-4 sm:-mx-8 mb-6 sm:mb-[30px]" />

        {/* Button - Same behavior as upload section: browse until uploaded, then continue */}
        <Button
          onClick={() => {
            if (uploadStatus === "uploaded") {
              handleNextStep()
            } else {
              handleBrowseClick()
            }
          }}
          className="h-12 sm:h-[55px] text-sm sm:text-base"
        >
          {uploadStatus === "uploaded" ? "Next Step" : uploadStatus === "uploading" ? "Uploading..." : "Continue"}
        </Button>
      </div>
    </div>
  )
}
